import { nullishFilter, HOUR } from '@caresend/utils';
import { distance } from '@turf/turf';
import { getStore } from '../../../../helpers/initStore.js';

const getEligibleShipmentsByProcedure = (instructionsByProcedure, shipmentInfoByType, placeGroupID, time) => {
    const eligibleShipmentsByProcedure = {};
    Object.entries(instructionsByProcedure).forEach(([procedureID, supplyInstructions]) => {
        var _a;
        const mailInInstructions = (_a = supplyInstructions === null || supplyInstructions === void 0 ? void 0 : supplyInstructions.supplyInstructionsByPlaceGroup[placeGroupID]) === null || _a === void 0 ? void 0 : _a.supplyInstructionsByDeliveryType.mailIn;
        if (!mailInInstructions)
            return;
        const { sampleStability } = mailInInstructions;
        const shipmentsMeetingSampleStabilityRequirement = [];
        Object.entries(shipmentInfoByType !== null && shipmentInfoByType !== void 0 ? shipmentInfoByType : {}).forEach(([__, shipmentInfo]) => {
            const { estimatedAcceptanceTimestamp } = shipmentInfo;
            const sampleStabilityInMilliseconds = sampleStability * HOUR;
            const isSampleStabilityMet = estimatedAcceptanceTimestamp - Number(time) < sampleStabilityInMilliseconds;
            if (isSampleStabilityMet)
                shipmentsMeetingSampleStabilityRequirement.push(shipmentInfo);
        });
        if (shipmentsMeetingSampleStabilityRequirement.length) {
            eligibleShipmentsByProcedure[procedureID] = shipmentsMeetingSampleStabilityRequirement;
        }
    });
    return eligibleShipmentsByProcedure;
};
const getLabShipmentsForPlaceGroup = (eligibleShipmentsByProcedure) => {
    const intersectionOfShipmentsForPlaceGroup = Object.values(eligibleShipmentsByProcedure).reduce((acc, shipmentInfos) => {
        shipmentInfos.forEach((shipmentInfo) => {
            if (!acc.some((info) => info.shipmentType === shipmentInfo.shipmentType))
                acc.push(shipmentInfo);
        });
        return acc;
    }, []);
    return {
        shipmentInfoByType: intersectionOfShipmentsForPlaceGroup.reduce((acc, shipmentInfo) => {
            acc[shipmentInfo.shipmentType] = shipmentInfo;
            return acc;
        }, {}),
    };
};
/**
 * Get the places by distance from the location
 */
const getPlaceIDsByDistance = (location, placesWithCoordinates, maxPlacesForOffice) => {
    const placeIDs = Object.keys(placesWithCoordinates);
    const locationCoordinates = [location.lng, location.lat];
    const placesWithDistance = placeIDs.map((placeID) => {
        const coords = placesWithCoordinates[placeID];
        if (!coords)
            return;
        const coordinates = [coords.lng, coords.lat];
        const kilometers = distance(locationCoordinates, coordinates);
        return { placeID, distance: kilometers };
    }).filter(nullishFilter);
    placesWithDistance.sort((a, b) => a.distance - b.distance);
    const filteredPlaces = maxPlacesForOffice
        ? placesWithDistance.slice(0, maxPlacesForOffice)
        : placesWithDistance;
    return filteredPlaces.map(({ placeID }) => placeID);
};
const getPlaceGroupIDsFromProcedures = (procedures) => Object.keys(procedures).reduce((placeGroupIDs, procedureID) => {
    var _a;
    const procedure = procedures[procedureID];
    if (!procedure)
        return placeGroupIDs;
    const placeGroupIDsForProcedure = Object.keys((_a = procedure.dropOffPlaceGroups) !== null && _a !== void 0 ? _a : {});
    placeGroupIDsForProcedure.forEach((placeGroupID) => {
        if (!placeGroupIDs.includes(placeGroupID)) {
            placeGroupIDs.push(placeGroupID);
        }
    });
    return placeGroupIDs;
}, []);
/**
 * Returns an error if the time slot is missing any drop off place groups
 */
const getPlaceGroupsMissingError = (timeSlotPlaceGroups, dropOffPlaceGroups) => {
    const missingPlaceGroups = dropOffPlaceGroups.filter((placeGroupID) => !timeSlotPlaceGroups.some(([id, _]) => id === placeGroupID));
    if (missingPlaceGroups.length) {
        return `Time slot is missing the following place groups: ${missingPlaceGroups.join(', ')}`;
    }
    return null;
};
/**
 * This function sets the eligible placeIDs for each time slot
 * as well as fetching the required places
 */
const setPlaces = async (placeIDsWithCoordinates, location, maxPlacesForOffice) => {
    const store = getStore();
    const filteredPlaceIDs = Object.keys(placeIDsWithCoordinates).length
        ? getPlaceIDsByDistance(location, placeIDsWithCoordinates, maxPlacesForOffice)
        : [];
    store.commit('schedulingFlow/SET_PLACE_IDS', filteredPlaceIDs);
    await store.dispatch('places/fetchPlaces', { placeIDs: filteredPlaceIDs, includePlaceGroups: true });
};

export { getEligibleShipmentsByProcedure, getLabShipmentsForPlaceGroup, getPlaceGroupIDsFromProcedures, getPlaceGroupsMissingError, setPlaces };
